<template>
	<CDialog
		custom-class="c-timeout-dialog"
		:visible="showTimeoutDialog"
		:title="resources?.YourSessionIsAboutToExpire || 'Your session is about to expire'"
		@done="onClose"
	>
		<div>
			{{ resources?.CloseThisDialogToRenewYourSessionAndContinue || 'Close this dialog to renew your session and continue.' }}
		</div>
	</CDialog>
</template>

<script lang="ts">
	import { defineComponent, ref, inject } from 'vue';
	import CDialog from 'src/components/dialogs/Dialog.vue';
	import { isServiceRequestError, serviceRequest } from '@cognitoforms/api/services/service-request';
	import SessionConsumer from 'src/mixins/session-consumer';
	import { autoLogout } from '@cognitoforms/api/services/session-service';
	import { getServerDate, refreshSession } from '@cognitoforms/api/services/session';
	import { SessionStorageKeys } from '@cognitoforms/api/services/session-storage-keys';
	import { localizationResourcesKey } from 'src/utilities/injection-keys';

	export default defineComponent({
		components: {
			CDialog
		},
		mixins: [SessionConsumer],
		props: {
			useTimer: {
				type: Boolean,
				default: true
			}
		},
		setup() {
			const resources = inject(localizationResourcesKey);

			const heartbeat = ref(null);
			const showTimeoutDialog = ref(false);

			return {
				resources,
				heartbeat,
				showTimeoutDialog
			};
		},
		watch: {
			session: {
				immediate: true,
				handler: function () {
					if (this.session && this.session.value)
						this.initialize();
				}
			}
		},
		destroyed () {
			if (this.heartbeat)
				clearInterval(this.heartbeat);
		},
		methods: {
			initialize() {
				// Don't initialize if heartbeat exists
				if (this.heartbeat)
					return;

				this.checkExpiration();
				this.heartbeat = this.useTimer ? setInterval(this.checkExpiration, 60000) : true;
			},
			async autoLogout() {
				localStorage.setItem(SessionStorageKeys.LastAutoLogout, getServerDate().toString());
				const returnUrl = encodeURIComponent(window.location.href);
				let requiredAuthMethod;
				let requiredAuthProvider;
				let orgName;
				let orgCode;
				try {
					let csrfToken = null;
					try {
						csrfToken = await serviceRequest('svc/csrf', null, { method: 'GET' });
					}
					catch (e) {}
					const response = await autoLogout(csrfToken);
					requiredAuthMethod = response?.requiredAuthMethod;
					requiredAuthProvider = response?.requiredAuthProvider;
					orgName = response?.orgName;
					orgCode = response?.orgCode;
				}
				catch (e) {
					if (isServiceRequestError(e)) {
						requiredAuthMethod = e.headers.get('Required-Auth-Method');
						requiredAuthProvider = e.headers.get('Required-Auth-Provider');
					}
				}
				finally {
					let logoutUrl = '/login?logoutReason=TokenTimeout';
					if ((orgName && orgCode) || requiredAuthMethod !== 'Custom')
						logoutUrl += `${requiredAuthMethod ? '&requiredAuthMethod=' + requiredAuthMethod : ''}${requiredAuthProvider ? '&authenticationProvider=' + requiredAuthProvider : ''}${orgCode ? '&organizationCode=' + orgCode : ''}${orgName ? '&destinationOrganization=' + orgName : ''}${returnUrl ? '&returnUrl=' + returnUrl : '' }`;
					else
						logoutUrl += `${returnUrl ? '&returnUrl=' + returnUrl : '' }`;
					window.location.assign(logoutUrl);
				}
			},
			async onClose() {
				this.showTimeoutDialog = false;
				try {
					await serviceRequest('session/keepalive', null, { method: 'GET' });
				}
				catch {
					// Log out if keepalive request wasn't successful
					await this.autoLogout();
				}
			},
			// Checks to see if our current session is at or near expiry. If it is, refresh it and check again to confirm
			async checkExpiration(refreshed: boolean = false) {
				// Calculate minutes to session expiry, mark the session as expired if this value is less than 0
				const minutesToExpiration = (this.session.value.expiration - getServerDate().getTime()) / 1000 / 60;
				let expired = minutesToExpiration < 0;

				// If any other tabs have been auto logged out since our session was created, mark the session as expired
				const lastLogout = localStorage.getItem(SessionStorageKeys.LastAutoLogout);
				if (lastLogout && this.session.value.created < new Date(lastLogout))
					expired = true;

				// If our session is good, exit early
				if (minutesToExpiration >= 5 && !expired)
					return;

				// If this check was performed before refreshing the session, refresh the session and check again
				if (!refreshed) {
					try {
						await refreshSession(true);
						this.$nextTick(() => this.checkExpiration(true));
						return;
					}
					// If we can't refresh the session, continue with original check
					catch { }
				}

				// If the session has expired, log out
				if (expired)
					this.autoLogout();
				// If the session is less than 5 minutes to expiry, show the timeout dialog
				else if (minutesToExpiration < 5 && !this.allowSessionRefresh) {
					this.showTimeoutDialog = true;
				}
			}
		}
	});
</script>
<style lang="scss">
.c-timeout-dialog {
	max-width: 600px;
}
</style>